// Theme preference: 'light' | 'dark' | 'system'. 'system' follows the OS via
// prefers-color-scheme and stays in sync. The resolved value ('light'|'dark') is
// written to <html data-theme>. Persisted per-device in localStorage; the account
// copy (agent.prefs.theme) is applied on load only when this device hasn't chosen.
import { record } from './activity.js';
import { EVENT } from '@essential-support/shared/constants';

const STORAGE_KEY = 'es_theme';
const MODES = ['light', 'dark', 'system'];
const mql = window.matchMedia('(prefers-color-scheme: dark)');
// The device's stored choice from the start (initTheme applies it): on a KB page this module
// loads late (the header island's Settings) and must not repaint the stored theme as "system".
let currentMode = MODES.includes(getStoredTheme()) ? getStoredTheme() : 'system';

function resolved(mode) {
  return mode === 'dark' || (mode === 'system' && mql.matches) ? 'dark' : 'light';
}
function paint() {
  document.documentElement.dataset.theme = resolved(currentMode);
}
// Every page's head also runs the pre-paint bootstrap (shared/src/kb/themeBootstrap.js), which
// re-resolves from localStorage ALONE on these same events. It was registered first, so it runs
// first; repainting after it keeps a theme this module applied without storing it (the account
// theme — App.jsx applyAccountChrome) instead of letting it flip back to the system one.
mql.addEventListener('change', paint);
window.addEventListener('pageshow', paint);
document.addEventListener('prerenderingchange', paint);

export function applyTheme(mode) {
  currentMode = MODES.includes(mode) ? mode : 'system';
  paint();
}
export function getStoredTheme() {
  try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
}
export function setStoredTheme(mode) {
  try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* ignore */ }
  record(EVENT.UI_THEME_CHANGED, { theme: mode }); // user toggle only (init uses getStoredTheme)
}
// Apply the device's stored theme (or system) as early as possible — avoids a flash.
export function initTheme() {
  applyTheme(getStoredTheme() || 'system');
}
